import { Injectable } from '@angular/core';

import { Entity, EntityQuery, FilterQueryOp, Predicate } from 'breeze-client';
import moment from 'moment';
import {
    Ach,
    AchAttachment,
    AchNote,
    AchTransaction,
    BuildAssembly,
    Contact,
    ContactRole,
    Customer,
    Dealer,
    DealerAttachment,
    EmailAttachment,
    InventoryProjection,
    InventoryProjectionLine,
    InventoryProjectionLinePart,
    InvoiceAttachment,
    InvoiceLine,
    Note,
    Order,
    OrderAttachment,
    OrderLineDetail,
    PackingSlip,
    PackingSlipAttachment,
    Part,
    PartAlias,
    PartAttachment,
    PartConfigAttachment,
    PartConfigAxle,
    PartConfigBrake,
    PartConfigCrossArm,
    PartConfigGeneralSetting,
    PartConfigModelSize,
    PartConfigModelType,
    PartConfigOption,
    PartConfigPlaceQuantity,
    PartConfigPlaceQuantityCrossArmLink,
    PartConfigSizeLink,
    PartConfigSuspension,
    PartConfigSuspensionTireLink,
    PartConfigTire,
    PartConfiguration,
    PartConfigurationLine,
    PartConfigWinchStand,
    PartConfigWinchStandOption,
    PartConfigWinchStandOptionLink,
    PartLink,
    PartPurchaseAttachment,
    PartPartServiceChargeLink,
    PartServiceCharge,
    Photo,
    PricingTier,
    PurchaseOrder,
    PurchaseOrderInvoiceLine,
    PurchaseOrderLine,
    PurchaseOrderPickUp,
    PurchaseOrderPickUpLine,
    PurchaseOrderStatus,
    Setting,
} from '../model/entity-model';
import { CustomerLocation, Entities, Guid } from './entities';
import { EntityManagerProvider } from './entity-manager-provider';
import { Repository } from './repository';
import { UnitOfWork } from './unit-of-work';

@Injectable()
export class GenesisUnitOfWork extends UnitOfWork {
    lookups: any;

    aches: Repository<Entity>;
    achAttachments: Repository<AchAttachment>;
    achTransactions: Repository<AchTransaction>;
    achNotes: Repository<AchNote>;

    contacts: Repository<Contact>;
    contactRoles: Repository<ContactRole>;
    partAliases: Repository<Entity>;
    partAttachments: Repository<PartAttachment>;
    parts: Repository<Entity>;
    partLinks: Repository<Entity>;
    partTypes: Repository<Entity>;
    partPartServiceChargeLinks: Repository<Entity>;
    partPartTypes: Repository<Entity>;
    partPurchases: Repository<Entity>;
    partPurchaseAttachments: Repository<PartPurchaseAttachment>;
    partServiceCharges: Repository<Entity>;
    orders: Repository<Entity>;
    orderAchPayments: Repository<Entity>;
    orderAttachments: Repository<Entity>;
    orderLines: Repository<Entity>;
    customers: Repository<Entity>;
    dealerAttachments: Repository<Entity>;
    dealers: Repository<Entity>;
    shipAddresses: Repository<Entity>;
    vins: Repository<Entity>;
    orderLineDetails: Repository<Entity>;
    orderLineDetailNotes: Repository<Entity>;
    orderLineDetailParts: Repository<Entity>;
    partOptions: Repository<Entity>;
    packingSlips: Repository<Entity>;
    emailAddresses: Repository<Entity>;
    emailAttachments: Repository<Entity>;
    emailMessages: Repository<Entity>;
    emailTemplates: Repository<Entity>;
    feeTypes: Repository<Entity>;
    settings: Repository<Setting>;
    salesReps: Repository<Entity>;
    stateTaxExemptions: Repository<Entity>;
    orderPaymentMethods: Repository<Entity>;
    invoices: Repository<Entity>;
    invoiceLines: Repository<Entity>;
    invoicePaymentMethods: Repository<Entity>;
    invoiceAttachments: Repository<Entity>;
    notes: Repository<Note>;

    purchaseOrders: Repository<Entity>;
    purchaseOrderLines: Repository<Entity>;
    purchaseOrderLineItems: Repository<Entity>;
    purchaseOrderLineReceivings: Repository<Entity>;
    purchaseOrderAttachments: Repository<Entity>;
    purchaseOrderInvoices: Repository<Entity>;
    purchaseOrderInvoiceLines: Repository<Entity>;
    purchaseOrderPickUps: Repository<Entity>;
    purchaseOrderPickUpLines: Repository<Entity>;
    vendors: Repository<Entity>;
    vendorTerms: Repository<Entity>;
    vendorPaymentMethods: Repository<Entity>;
    users: Repository<Entity>;
    userRoles: Repository<Entity>;
    eventLogs: Repository<Entity>;
    purchaseOrderShipAddresses: Repository<Entity>;

    assemblyJigs: Repository<Entity>;
    assemblySchedules: Repository<Entity>;
    assemblyJigMembers: Repository<Entity>;
    assemblyJigNotes: Repository<Entity>;

    confirmations: Repository<Entity>;
    confirmationMessages: Repository<Entity>;
    confirmationPaymentMethods: Repository<Entity>;
    dealerTerms: Repository<Entity>;
    deliveries: Repository<Entity>;
    deliveryDrivers: Repository<Entity>;
    deliveryOptions: Repository<Entity>;
    deliveryStacks: Repository<Entity>;
    deliveryTrailers: Repository<Entity>;
    deliveryTrucks: Repository<Entity>;
    deliveryTransportPlates: Repository<Entity>;

    buildAssemblies: Repository<Entity>;
    buildAssemblyDetails: Repository<Entity>;
    partBulkEdits: Repository<Entity>;

    payments: Repository<Entity>;
    paymentLines: Repository<Entity>;
    paymentCredits: Repository<Entity>;
    paymentCreditTypes: Repository<Entity>;
    invoiceFees: Repository<Entity>;
    creditCards: Repository<Entity>;

    contactLogs: Repository<Entity>;
    contactMethods: Repository<Entity>;
    contactTypes: Repository<Entity>;

    expenseReports: Repository<Entity>;
    expenseReportLines: Repository<Entity>;
    expenseTypes: Repository<Entity>;
    expenseReportLineAttachments: Repository<Entity>;
    holdNotes: Repository<Entity>;
    assemblyJigLocks: Repository<Entity>;

    partConfigAttachments: Repository<PartConfigAttachment>;
    partConfirgurations: Repository<PartConfiguration>;
    partConfigurationLines: Repository<PartConfigurationLine>;

    partConfigAxles: Repository<PartConfigAxle>;
    partConfigBrakes: Repository<PartConfigBrake>;
    partConfigModelSizes: Repository<PartConfigModelSize>;
    partConfigModelTypes: Repository<PartConfigModelType>;
    partConfigOptions: Repository<PartConfigOption>;
    partConfigSizeLinks: Repository<PartConfigSizeLink>;
    partConfigSuspensions: Repository<PartConfigSuspension>;
    partConfigSuspensionTireLinks: Repository<PartConfigSuspensionTireLink>;
    partConfigTires: Repository<PartConfigTire>;
    partConfigWinchStands: Repository<PartConfigWinchStand>;
    partConfigWinchStandOptions: Repository<PartConfigWinchStandOption>;
    partConfigWinchStandOptionLinks: Repository<PartConfigWinchStandOptionLink>;
    partConfigCrossArms: Repository<PartConfigCrossArm>;
    partConfigPlaceQuantities: Repository<PartConfigPlaceQuantity>;
    partConfigPlaceQuantityCrossArmLinks: Repository<PartConfigPlaceQuantityCrossArmLink>;
    partConfigGeneralSettings: Repository<PartConfigGeneralSetting>;

    inventoryProjections: Repository<InventoryProjection>;
    inventoryProjectionLines: Repository<InventoryProjectionLine>;
    inventoryProjectionLineParts: Repository<InventoryProjectionLinePart>;

    pricingTiers: Repository<PricingTier>;

    constructor(emProvider: EntityManagerProvider) {
        super(emProvider);

        this.lookups = Entities.lookups;
        emProvider.prepare().then((lookups) => {
            this.lookups = lookups;
        });
        this.contacts = this.createRepository<Contact>('Contact', 'contacts');
        this.contactLogs = this.createRepository<Entity>('ContactLog', 'contactLogs');
        this.contactMethods = this.createRepository<Entity>('ContactMethod', 'contactMethods');
        this.contactRoles = this.createRepository<Contact>('ContactRole', 'contactRoles');
        this.contactTypes = this.createRepository<Entity>('ContactType', 'contactTypes');
        this.aches = this.createRepository<Entity>('Ach', 'aches');
        this.achAttachments = this.createRepository<AchAttachment>('AchAttachment', 'achAttachments');
        this.achTransactions = this.createRepository<AchTransaction>('AchTransaction', 'achTransactions');
        this.achNotes = this.createRepository<AchNote>('AchNote', 'achNotes');

        this.partAliases = this.createRepository<Entity>('PartAlias', 'partAliases');
        this.partAttachments = this.createRepository<PartAttachment>('PartAttachment', 'partAttachments');
        this.parts = this.createRepository<Entity>('Part', 'parts');
        this.partLinks = this.createRepository<Entity>('PartLink', 'partLinks');
        this.partTypes = this.createRepository<Entity>('PartType', 'partTypes');
        this.partPartServiceChargeLinks = this.createRepository<Entity>('PartPartServiceChargeLink', 'partPartServiceChargeLinks');
        this.partPartTypes = this.createRepository<Entity>('PartPartType', 'partPartTypes');
        this.partPurchases = this.createRepository<Entity>('PartPurchase', 'partPurchases');
        this.partPurchaseAttachments = this.createRepository<PartPurchaseAttachment>('PartPurchaseAttachment', 'partPurchaseAttachments');
        this.partServiceCharges = this.createRepository<Entity>('PartServiceCharge', 'partServiceCharges');
        this.orders = this.createRepository<Entity>('Order', 'orders');
        this.orderAchPayments = this.createRepository<Entity>('OrderAchPayment', 'orderAchPayments');
        this.orderLines = this.createRepository<Entity>('OrderLine', 'orderLines');
        this.orderAttachments = this.createRepository<Entity>('OrderAttachment', 'orderAttachments');
        this.customers = this.createRepository<Entity>('Customer', 'customers');
        this.dealerAttachments = this.createRepository<Entity>('DealerAttachment', 'dealerAttachments');
        this.dealers = this.createRepository<Entity>('Dealer', 'dealers');
        this.shipAddresses = this.createRepository<Entity>('ShipAddress', 'shipAddresses');
        this.vins = this.createRepository<Entity>('Vin', 'vins');
        this.orderLineDetails = this.createRepository<Entity>('OrderLineDetail', 'orderLineDetails');
        this.orderLineDetailNotes = this.createRepository<Entity>('OrderLineDetailNote', 'orderLineDetailNotes');
        this.partOptions = this.createRepository<Entity>('PartOption', 'partOptions');
        this.packingSlips = this.createRepository<Entity>('PackingSlip', 'packingSlips');
        this.purchaseOrders = this.createRepository<Entity>('PurchaseOrder', 'purchaseOrders');
        this.purchaseOrderAttachments = this.createRepository<Entity>('PurchaseOrderAttachment', 'purchaseOrderAttachments');
        this.purchaseOrderLines = this.createRepository<Entity>('PurchaseOrderLine', 'purchaseOrderLines');
        this.purchaseOrderLineItems = this.createRepository<Entity>('PurchaseOrderLineItem', 'purchaseOrderLineItems');
        this.purchaseOrderLineReceivings = this.createRepository<Entity>('PurchaseOrderLineReceiving', 'purchaseOrderLineReceivings');
        this.vendors = this.createRepository<Entity>('Vendor', 'vendors');
        this.vendorTerms = this.createRepository<Entity>('VendorTerm', 'vendorTerms');
        this.vendorPaymentMethods = this.createRepository<Entity>('VendorPaymentMethod', 'vendorPaymentMethods');
        this.users = this.createRepository<Entity>('User', 'users');
        this.userRoles = this.createRepository<Entity>('UserRole', 'userRoles');
        this.orderLineDetailParts = this.createRepository<Entity>('OrderLineDetailPart', 'orderLineDetailParts');
        this.emailAddresses = this.createRepository<Entity>('EmailAddress', 'emailAddresses');
        this.emailAttachments = this.createRepository<Entity>('EmailAttachment', 'emailAttachments');
        this.emailMessages = this.createRepository<Entity>('EmailMessage', 'emailMessages');
        this.emailTemplates = this.createRepository<Entity>('EmailTemplate', 'emailTemplates');
        this.settings = this.createRepository<Setting>('Setting', 'settings');
        this.eventLogs = this.createRepository<Entity>('EventLog', 'eventLogs');

        this.assemblyJigs = this.createRepository<Entity>('AssemblyJig', 'assemblyJigs');
        this.assemblySchedules = this.createRepository<Entity>('AssemblySchedule', 'assemblySchedules');
        this.assemblyJigMembers = this.createRepository<Entity>('AssemblyJigMember', 'assemblyJigMembers');
        this.assemblyJigNotes = this.createRepository<Entity>('AssemblyJigNote', 'assemblyJigNotes');

        this.salesReps = this.createRepository<Entity>('SalesRep', 'salesReps');
        this.stateTaxExemptions = this.createRepository<Entity>('StateTaxExemption', 'stateTaxExemptions');
        this.confirmations = this.createRepository<Entity>('Confirmation', 'confirmations');
        this.confirmationMessages = this.createRepository<Entity>('ConfirmationMessage', 'confirmationMessages');
        this.confirmationPaymentMethods = this.createRepository<Entity>('ConfirmationPaymentMethod', 'confirmationPaymentMethods');
        this.orderPaymentMethods = this.createRepository<Entity>('OrderPaymentMethod', 'orderPaymentMethods');
        this.buildAssemblies = this.createRepository<Entity>('BuildAssembly', 'buildAssemblies');
        this.buildAssemblyDetails = this.createRepository<Entity>('BuildAssemblyDetail', 'buildAssemblyDetails');
        this.partBulkEdits = this.createRepository<Entity>('PartBulkEdit', 'partBulkEdits');
        this.dealerTerms = this.createRepository<Entity>('DealerTerm', 'dealerTerms');
        this.deliveryDrivers = this.createRepository<Entity>('DeliveryDriver', 'deliveryDrivers');
        this.deliveryOptions = this.createRepository<Entity>('DeliveryOption', 'deliveryOptions');
        this.deliveryTrailers = this.createRepository<Entity>('DeliveryTrailer', 'deliveryTrailers');
        this.deliveryTransportPlates = this.createRepository<Entity>('DeliveryTransportPlate', 'deliveryTransportPlates');
        this.deliveryTrucks = this.createRepository<Entity>('DeliveryTruck', 'deliveryTrucks');
        this.payments = this.createRepository<Entity>('Payment', 'payments');
        this.paymentLines = this.createRepository<Entity>('PaymentLine', 'paymentLines');
        this.paymentCredits = this.createRepository<Entity>('PaymentCredit', 'paymentCredits');
        this.paymentCreditTypes = this.createRepository<Entity>('PaymentCreditType', 'paymentCreditTypes');
        this.invoicePaymentMethods = this.createRepository<Entity>('InvoicePaymentMethod', 'invoicePaymentMethods');
        this.purchaseOrderInvoices = this.createRepository<Entity>('PurchaseOrderInvoice', 'purchaseOrderInvoices');
        this.purchaseOrderInvoiceLines = this.createRepository<Entity>('PurchaseOrderInvoiceLine', 'purchaseOrderInvoiceLines');
        this.purchaseOrderPickUps = this.createRepository<Entity>('PurchaseOrderPickUp', 'purchaseOrderPickUps');
        this.purchaseOrderPickUpLines = this.createRepository<Entity>('PurchaseOrderPickUpLine', 'purchaseOrderPickUpLines');
        this.deliveries = this.createRepository<Entity>('Delivery', 'deliveries');
        this.deliveryStacks = this.createRepository<Entity>('DeliveryStack', 'deliveryStacks');
        this.feeTypes = this.createRepository<Entity>('FeeType', 'feeTypes');
        this.invoiceFees = this.createRepository<Entity>('InvoiceFee', 'invoiceFees');
        this.creditCards = this.createRepository<Entity>('CreditCard', 'creditCards');
        this.purchaseOrderShipAddresses = this.createRepository<Entity>('PurchaseOrderShipAddress', 'purchaseOrderShipAddresses');

        this.invoices = this.createRepository<Entity>('Invoice', 'invoices');
        this.invoiceLines = this.createRepository<Entity>('InvoiceLine', 'invoiceLines');
        this.invoiceAttachments = this.createRepository<Entity>('InvoiceAttachment', 'invoiceAttachments');
        this.notes = this.createRepository<Note>('Note', 'notes');

        this.expenseReports = this.createRepository<Entity>('ExpenseReport', 'expenseReports');
        this.expenseReportLines = this.createRepository<Entity>('ExpenseReportLine', 'expenseReportLines');
        this.expenseTypes = this.createRepository<Entity>('ExpenseType', 'expenseTypes');
        this.expenseReportLineAttachments = this.createRepository<Entity>('ExpenseReportLineAttachment', 'expenseReportLineAttachments');
        this.holdNotes = this.createRepository<Entity>('HoldNote', 'holdNotes');
        this.assemblyJigLocks = this.createRepository<Entity>('AssemblyJigLock', 'assemblyJigLocks');

        this.partConfigAttachments = this.createRepository<PartConfigAttachment>('PartConfigAttachment', 'partConfigAttachments');
        this.partConfirgurations = this.createRepository<PartConfiguration>('PartConfiguration', 'partConfirgurations');
        this.partConfigurationLines = this.createRepository<PartConfigurationLine>('PartConfigurationLine', 'partConfigurationLines');

        this.partConfigAxles = this.createRepository<PartConfigAxle>('PartConfigAxle', 'partConfigAxles');
        this.partConfigBrakes = this.createRepository<PartConfigBrake>('PartConfigBrake', 'partConfigBrakes');
        this.partConfigModelSizes = this.createRepository<PartConfigModelSize>('PartConfigModelSize', 'partConfigModelSizes');
        this.partConfigModelTypes = this.createRepository<PartConfigModelType>('PartConfigModelType', 'partConfigModelTypes');
        this.partConfigOptions = this.createRepository<PartConfigOption>('PartConfigOption', 'partConfigOptions');
        this.partConfigSizeLinks = this.createRepository<PartConfigSizeLink>('PartConfigSizeLink', 'partConfigSizeLinks');
        this.partConfigSuspensions = this.createRepository<PartConfigSuspension>('PartConfigSuspension', 'partConfigSuspensions');
        this.partConfigSuspensionTireLinks = this.createRepository<PartConfigSuspensionTireLink>('PartConfigSuspensionTireLink', 'partConfigSuspensionTireLinks');
        this.partConfigTires = this.createRepository<PartConfigTire>('PartConfigTire', 'partConfigTires');
        this.partConfigWinchStands = this.createRepository<PartConfigWinchStand>('PartConfigWinchStand', 'partConfigWinchStands');
        this.partConfigWinchStandOptions = this.createRepository<PartConfigWinchStandOption>('PartConfigWinchStandOption', 'partConfigWinchStandOptions');
        this.partConfigWinchStandOptionLinks = this.createRepository<PartConfigWinchStandOptionLink>('PartConfigWinchStandOptionLink', 'partConfigWinchStandOptionLinks');
        this.partConfigCrossArms = this.createRepository<PartConfigCrossArm>('PartConfigCrossArm', 'partConfigCrossArms');
        this.partConfigPlaceQuantities = this.createRepository<PartConfigPlaceQuantity>('PartConfigPlaceQuantity', 'partConfigPlaceQuantities');
        this.partConfigPlaceQuantityCrossArmLinks = this.createRepository<PartConfigPlaceQuantityCrossArmLink>(
            'PartConfigPlaceQuantityCrossArmLink',
            'partConfigPlaceQuantityCrossArmLinks',
        );
        this.partConfigGeneralSettings = this.createRepository<PartConfigGeneralSetting>('PartConfigGeneralSetting', 'partConfigGeneralSettings');

        this.inventoryProjections = this.createRepository<InventoryProjection>('InventoryProjection', 'inventoryProjections');
        this.inventoryProjectionLines = this.createRepository<InventoryProjectionLine>('InventoryProjectionLine', 'inventoryProjectionLines');
        this.inventoryProjectionLineParts = this.createRepository<InventoryProjectionLinePart>('InventoryProjectionLinePart', 'inventoryProjectionLineParts');

        this.pricingTiers = this.createRepository<PricingTier>('PricingTier', 'pricingTiers');
    }

    // async getWorkOrderList() {
    //     const query = EntityQuery.from('WorkOrderList');
    //     const data = await this.manager.executeQuery(query);
    //     return data.results;
    // }
    async getGuid() {
        const query = EntityQuery.from('GetGuid');
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    async getParts(options?) {
        // parts() on the server side excludes deleted parts
        let query = EntityQuery.from('Parts').orderBy('partNumber');
        console.log('options', options);
        if (!!options) {
            if (options.expand) query = query.expand(options.expand);
            if (options.partTypeIds) query = query.where('partPartTypes', 'any', 'partTypeId', 'in', options.partTypeIds);
            if (options.partNumber) query = query.where('partNumber', FilterQueryOp.Contains, options.partNumber);
            if (options.isAssembly) query = query.where('isAssembly', FilterQueryOp.Equals, 'true');
        }

        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartOptions() {
        let predicate = new Predicate('partNumber', FilterQueryOp.StartsWith, 'OPT-');
        let query = EntityQuery.from('Parts').orderBy('partNumber').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartByPartNumber(partNumber: string) {
        let query = EntityQuery.from('Parts').where('partNumber', FilterQueryOp.Equals, partNumber);
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    async getPartsByPartNumberRegex(partNumber: string) {
        let query = EntityQuery.from('GetPartsByPartNumberRegex').withParameters({ partNumber: partNumber }).orderBy('partNumber');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartsForPurchaseOrder(vendorId: number) {
        // parts() on the server side excludes deleted parts
        let query = EntityQuery.from('GetPartsForPurchaseOrder').withParameters({ vendorId: vendorId }).orderBy('partNumber');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartsForSearch(options?) {
        // parts() on the server side excludes deleted parts
        let query = EntityQuery.from('PartsForSearch');

        // if (!!options) {
        //     if (options.expand) query = query.expand(options.expand);
        //     if (options.partTypeIds) query = query.where('partPartTypes', 'any', 'partTypeId', 'in', options.partTypeIds);
        // }

        if (options) {
            if (options.excludePartIds) {
                const predicate = new Predicate('id', 'in', options.excludePartIds).not();
                query = query.where(predicate);
            }
            if (options.expand) query = query.expand(options.expand);
        }

        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartBulkEdits() {
        const dateCheck = moment(new Date()).add(-10, 'days').toDate();
        let predicate = new Predicate('sent', '==', null).or('processed', '==', null).or('processed', '>=', dateCheck);
        const query = EntityQuery.from('PartBulkEdits').expand('part, orderLineDetail.orderLine.order.dealer, orderLineDetail.orderLine.part').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOpenPurchaseOrdersByPartIds(partIds: number[]) {
        const query = EntityQuery.from('GetOpenPurchaseOrdersByPartIds').withParameters({ partIds });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getAliasByPartId(partId: number) {
        let predicate = new Predicate('deleted', '==', null).and('partId', '==', partId);
        const query = EntityQuery.from('PartAliases').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartInventory() {
        const query = EntityQuery.from('PartInventory').expand('purchaseOrderLines.purchaseOrder, childrenParts.purchaseOrderLines.purchaseOrder');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getInventoryUsage(start, end, includeNonTracked) {
        const startDate = moment(start).startOf('day');
        const endDate = moment(end).startOf('day');
        const query = EntityQuery.from('InventoryUsage').withParameters({ startDate, endDate, includeNonTracked });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartLinkByPartId(partId: number) {
        let predicate = new Predicate('deleted', '==', null).and('partId', '==', partId);
        const query = EntityQuery.from('PartLinks').where(predicate).expand('subPart.partLinks');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getPartLinksByPartId(partId: number) {
        const query = EntityQuery.from('GetPartLinkDetails').withParameters({ partId: partId }).expand('subPart.partLinks.subPart');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getPartLinkByPartIds(partIds: number[]) {
        const query = EntityQuery.from('PartLinks').where('partId', 'in', partIds).expand('subPart.partLinks.subPart');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getActivePartLinksBySubPartIds(subPartIds: number[]) {
        const predicate = new Predicate('deleted', FilterQueryOp.Equals, null).and('subPartId', 'in', subPartIds);
        const query = EntityQuery.from('PartLinks').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results as PartLink[];
    }

    async getFrameParts() {
        const query = EntityQuery.from('getFramePartInfo');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getPartFrames() {
        const query = EntityQuery.from('getPartFrameInfo');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartPurchases() {
        const query = EntityQuery.from('PartPurchases').expand('part, partPurchaseAttachments');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartOptionsByPartId(partId: number) {
        let predicate = new Predicate('deleted', '==', null).and('partId', '==', partId).and('optionPart.deleted', '==', null).and('part.deleted', '==', null);
        const query = EntityQuery.from('PartOptions').where(predicate).expand('optionPart').orderBy('optionPart.partNumber');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartsByPartTypeId(partTypeId: number) {
        const query = EntityQuery.from('PartPartTypes').where('id', '==', partTypeId).expand('part');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getBuildAssemblies(showCompleted?: boolean) {
        const today = moment(new Date()).startOf('day').add(-1, 'days').toDate();
        let orPredicate = new Predicate('statusId', '!=', BuildAssembly.Statuses.Complete).or('createDate', '>', today);
        let predicate = new Predicate('deleted', '==', null);
        if (!showCompleted) predicate = predicate.and(orPredicate);
        const query = EntityQuery.from('BuildAssemblies').orderByDesc('id').where(predicate).expand('vendor, purchaseOrder');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOrders(expand?: any, showCompleted?: boolean) {
        let predicate = new Predicate('deleted', '==', null).and('statusId', '!=', Order.Statuses.Canceled);
        if (!showCompleted) predicate = predicate.and('statusId', '!=', Order.Statuses.Complete);
        const query = EntityQuery.from('Orders').orderByDesc('invoiceNumber').expand(expand).where(predicate);
        const data = await this.manager.executeQuery(query);
        return <Order[]>data.results;
    }

    async getSalesOrdersForCommissions() {
        let predicate = new Predicate('deleted', '==', null).and('statusId', '!=', Order.Statuses.Canceled).and('typeId', '==', Order.Types.SalesOrder);
        predicate = predicate.and('statusId', '!=', Order.Statuses.Complete);
        const query = EntityQuery.from('Orders').orderByDesc('invoiceNumber').expand('orderLines').where(predicate);
        const data = await this.manager.executeQuery(query);
        return <Order[]>data.results;
    }

    async getOrdersByDealer(dealerId: number) {
        const predicate = new Predicate('deleted', '==', null).and('statusId', '!=', Order.Statuses.Canceled).and('dealerId', '==', dealerId);
        const query = EntityQuery.from('Orders').where(predicate).orderByDesc('invoiceNumber');
        const data = await this.manager.executeQuery(query);
        return data.results as Order[];
    }

    async getOrderAttachments(orderId: number) {
        const predicate = new Predicate('orderId', '==', orderId).and('deleted', '==', null);
        const query = EntityQuery.from('OrderAttachments').where(predicate).orderBy('name');
        const data = await this.manager.executeQuery(query);
        return data.results as OrderAttachment[];
    }

    async getSchedulerData(statusNames: string[]) {
        let query = EntityQuery.from('SchedulerData');
        if (statusNames) query = query.where('statusName', 'in', statusNames);
        const data = await this.manager.executeQuery(query);
        return data.results as any[];
    }

    async getConfirmations(expand?: any, showCompleted?: boolean) {
        let predicate = new Predicate('deleted', '==', null).and('salesRepConfirmation', '==', false);
        if (!showCompleted) predicate = predicate.and('acknowledged', '==', null);
        const query = EntityQuery.from('Confirmations').orderByDesc('created').expand(expand).where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getConfirmationsByOrderId(orderId: number, includeSalesRepConfirmations = false) {
        let predicate = new Predicate('deleted', '==', null).and('orderId', '==', orderId);
        if (!includeSalesRepConfirmations) predicate = predicate.and('salesRepConfirmation', '==', false);
        const query = EntityQuery.from('Confirmations').expand('aches').orderByDesc('created').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getConfirmationMessages(includeArchived: boolean = false) {
        let predicate = new Predicate('deleted', '==', null);
        if (!includeArchived) predicate = predicate.and('archived', '==', null);
        const query = EntityQuery.from('ConfirmationMessages').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getAches(expand?: any, excludeAcknowledged: boolean = false) {
        let predicate = new Predicate('deleted', '==', null);
        let query = EntityQuery.from('Aches').orderByDesc('created').expand(expand).where(predicate);
        if (excludeAcknowledged) query = query.where('completed', '==', null);
        const data = await this.manager.executeQuery(query);
        return data.results as Ach[];
    }

    async getAchDetailStatusOverview() {
        let query = EntityQuery.from('GetAchDetailStatusOverview');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getAchAttachments(achId: number) {
        let predicate = new Predicate('deleted', '==', null).and('achId', '==', achId);
        const query = EntityQuery.from('AchAttachments').orderByDesc('created').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results as AchAttachment[];
    }

    async getAchTransactionsByInvoiceId(invoiceId: number, expand?: any, excludeAcknowledged: boolean = false) {
        let predicate = new Predicate('deleted', '==', null).and('invoiceId', '==', invoiceId);
        let query = EntityQuery.from('AchTransactions').expand(expand).where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results as AchTransaction[];
    }

    async getAchNotes(expand?: any) {
        let predicate = new Predicate('deleted', '==', null);
        let query = EntityQuery.from('AchNotes').orderByDesc('created').expand(expand).where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results as AchNote[];
    }

    async getEmails(expand?: any, take?: number) {
        let query = EntityQuery.from('EmailMessages').orderByDesc('created');
        if (expand) query = query.expand(expand);
        if (take) query = query.take(take);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getUserInfos(expand?: any) {
        const query = EntityQuery.from('Users').select('id, userName');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getUsers(expand?: any, options?: any) {
        let query = EntityQuery.from('Users').orderBy('userName');
        if (expand) query = query.expand(expand);

        if (options) {
            if (options.orderby) {
                query = query.orderBy(options.orderby);
            }
            if (options.notId) {
                query = query.where('id', FilterQueryOp.NotEquals, options.notId);
            }
            if (options.userName) {
                query = query.where('userName', FilterQueryOp.Equals, options.userName);
            }
            if (options.active === true) {
                query = query.where('active', FilterQueryOp.Equals, true);
            }
        }

        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getActiveOrderLines(expand?: any) {
        const query = EntityQuery.from('ActiveOrderLines');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getActiveOrderLineDetailNotes(expand?: any) {
        const query = EntityQuery.from('ActiveOrderLineDetailNotes');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOrderBuildDetailById(id: number, expand?: any) {
        const predicate = new Predicate('id', '==', id).and('isOption', '==', false);
        const query = EntityQuery.from('OrderBuildDetail').where(predicate).expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    async getOrderBuildDetail(expand?: any, showCompleted?: boolean, includeNonManufactured?: boolean) {
        let predicate = new Predicate('deleted', '==', null).and('isOption', '==', false);
        if (!showCompleted) predicate = predicate.and('statusId', '!=', OrderLineDetail.Statuses.Complete).and('statusId', '!=', OrderLineDetail.Statuses.DeliveredPickedUp);
        if (!includeNonManufactured) predicate = predicate.and('orderLine.isNonManufactured', '==', false);
        const query = EntityQuery.from('OrderBuildDetail').where(predicate).orderByDesc('orderLine.order.invoiceNumber').expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOrderBuildDetailByDates(filter: any, dateField: string, expand?: any) {
        let predicate = new Predicate('deleted', '==', null).and('isOption', '==', false).and('statusId', '!=', OrderLineDetail.Statuses.Complete);

        const orPredicate = new Predicate(dateField, '<=', filter.endDate).or(dateField, '==', null);
        predicate = predicate.and(orPredicate);

        const query = EntityQuery.from('OrderBuildDetail').where(predicate).orderByDesc(dateField).expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOrderLineDetailTrailers(filter?: any) {
        let query = EntityQuery.from('OrderLineDetailTrailers').where('deleted', '==', null).expand(['orderLine.order.dealer', 'packingSlip']);
        if (filter?.dealer) query = query.where('orderLine.order.dealer.companyName', FilterQueryOp.Contains, filter.dealer);
        if (filter?.invoiceNumber) query = query.where('orderLine.order.invoiceNumber', '==', Number(filter.invoiceNumber));
        if (filter?.vin) query = query.where('vIN', FilterQueryOp.EndsWith, filter.vin);
        const data = this.manager.executeQuery(query);
        return (await data).results as OrderLineDetail[];
    }

    async getActiveOrderBuildDetail(expand?: any) {
        let predicate = new Predicate('deleted', '==', null).and('isOption', '==', false);
        let statusPredicate = new Predicate('statusId', '==', OrderLineDetail.Statuses.New)
            .or('statusId', '==', OrderLineDetail.Statuses.Winter)
            .or('statusId', '==', OrderLineDetail.Statuses.WaitingOn);
        predicate = predicate.and(statusPredicate);
        const query = EntityQuery.from('OrderBuildDetail').where(predicate).orderByDesc('orderLine.order.invoiceNumber').expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOrderBuildDetailByOrderId(orderId: number) {
        const predicate = new Predicate('orderLine.orderId', '==', orderId)
            .and('deleted', '==', null)
            .and('isOption', '==', false)
            .and('isReplacement', '==', false)
            .and('isMisc', '==', false);
        const query = EntityQuery.from('OrderBuildDetail')
            .where(predicate)
            .orderByDesc('orderLine.order.invoiceNumber')
            .expand(['orderLine.order.dealer', 'status', 'packingSlip', 'notes.createdBy', 'optionDetails.orderLine']);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOrderLineDetailByOrderId(orderId: number, expand?: any) {
        const predicate = new Predicate('orderLine.orderId', '==', orderId).and('deleted', '==', null);
        let query = EntityQuery.from('OrderLineDetails').where(predicate);

        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOrderLineDetailsForInvoicingByOrderIds(orderIds: number[]) {
        let query = EntityQuery.from('GetOrderLineDetailsForInvoicingByOrderIds').withParameters({ orderIds });
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    async getOrderLineDetailsForInvoicingByPackingSlipIds(packingSlipIds: number[]) {
        let query = EntityQuery.from('GetOrderLineDetailsForInvoicingByPackingSlipIds').withParameters({ packingSlipIds });
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    async getOrderLineDetailByOrderIds(orderIds: number[], expand?: any) {
        const predicate = new Predicate('orderLine.orderId', 'in', orderIds);
        let query = EntityQuery.from('OrderLineDetails').where(predicate);

        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOrderLineDetailsByOrderLineId(orderLineId: number, expand?: string | string[]) {
        const predicate = new Predicate('orderLineId', '==', orderLineId);
        let query = EntityQuery.from('OrderLineDetails').where(predicate);
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getDealerOrderLineDetails(dealerId: number, expand?: string) {
        let predicate = new Predicate('deleted', '==', null).and('orderLine.order.dealerId', '==', dealerId).and('parentLineId', '==', null).and('delivered', '==', null);
        let query = EntityQuery.from('OrderLineDetails').where(predicate);
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results as OrderLineDetail[];
    }

    async getAssemblyJigTimes(date: Date) {
        let query = EntityQuery.from('GetAssemblyJigBuildTimesByDate').withParameters({ date: date });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOrderLineDetailsUncompletedOnPreviousDays(date: Date, expand?: string) {
        let predicate = new Predicate('deleted', '==', null).and('built', '==', null).and('isOption', '==', false).and('orderLine.part.isNonManufactured', '==', false);
        const start = moment(date).startOf('day').toDate();
        predicate = predicate;
        predicate = predicate.and('scheduled', '<', start);
        let query = EntityQuery.from('OrderLineDetails').where(predicate);
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results as OrderLineDetail[];
    }

    async getOrderLineDetailsByDate(date: Date, expand?: string, showBuilt?: boolean) {
        let predicate = new Predicate('deleted', '==', null).and('isOption', '==', false);
        const start = moment(date).startOf('day').toDate();
        const end = moment(start).add(1, 'days').toDate();
        if (!showBuilt) predicate = predicate.and('built', '==', null);
        predicate = predicate.and('scheduled', '>=', start).and('scheduled', '<', end);
        let query = EntityQuery.from('OrderLineDetails').where(predicate);
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results as OrderLineDetail[];
    }

    // number of details scheduled on any jig per day for computing build scheduler projection
    async getScheduledCountsByDay(startDate: Date, endDate: Date) {
        const start = moment(startDate).startOf('day').toDate();
        const end = moment(endDate).startOf('day').add(1, 'days').toDate();
        const predicate = new Predicate('deleted', '==', null)
            .and('isOption', '==', false)
            .and('assemblyJigId', '!=', null)
            .and('scheduled', '>=', start)
            .and('scheduled', '<', end);
        const query = EntityQuery.from('OrderLineDetails').where(predicate).select('id, scheduled');
        const data = await this.manager.executeQuery(query);
        const counts: { [day: string]: number } = {};
        data.results.forEach((row: any) => {
            const day = moment(row.scheduled).format('YYYY-MM-DD');
            counts[day] = (counts[day] || 0) + 1;
        });
        return counts;
    }

    async getAssemblyJigLocks(startDate: Date, endDate: Date) {
        const start = moment(startDate).startOf('day').toDate();
        const end = moment(endDate).startOf('day').add(1, 'days').toDate();
        const query = EntityQuery.from('AssemblyJigLocks').where(new Predicate('date', '>=', start).and('date', '<', end));
        const data = await this.manager.executeQuery(query);
        return data.results as any[];
    }

    async getOrderLineDetailsByAssemblyJigId(jigId: number, expand?: string) {
        let predicate = new Predicate('deleted', '==', null).and('isOption', '==', false).and('assemblyJigId', '==', jigId).and('built', '==', null);
        let query = EntityQuery.from('OrderLineDetails').where(predicate);
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results as OrderLineDetail[];
    }

    async getOrderLineDetailsByJigAndDate(date: Date, jigId: number, expand?: string, showBuilt?: boolean) {
        let predicate = new Predicate('deleted', '==', null).and('isOption', '==', false).and('assemblyJigId', '==', jigId);
        const start = moment(date).startOf('day').toDate();
        const end = moment(start).add(1, 'days').toDate();
        predicate = predicate.and('scheduled', '>=', start).and('scheduled', '<', end);

        let query = EntityQuery.from('OrderLineDetails').where(predicate);
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results as OrderLineDetail[];
    }

    async getOrderLineDetailsForBuildScheduler(expand?: string, options?: any) {
        let predicate = new Predicate('deleted', '==', null).and('built', '==', null).and('isOption', '==', false).and('orderLine.part.isNonManufactured', '==', false);
        if (options?.dealerId) predicate = predicate.and('orderLine.order.dealerId', '==', options.dealerId);

        if (!options?.dealerId && options?.dealerName) {
            if (!options.dealerFilterOp || options.dealerFilterOp === 'Contains')
                predicate = predicate.and('orderLine.order.dealer.companyName', FilterQueryOp.Contains, options.dealerName);
            if (options.dealerFilterOp === 'Does Not Contain') {
                const predicateNotContains = new Predicate('orderLine.order.dealer.companyName', FilterQueryOp.Contains, options.dealerName).not();
                predicate = predicate.and(predicateNotContains);
            }
        }

        if (options?.salesOrderNumber) predicate = predicate.and('orderLine.order.invoiceNumber', '==', options.salesOrderNumber);
        if (options?.partNumber) {
            predicate = predicate.and('orderLine.partNumber', FilterQueryOp.Contains, options.partNumber);
        }
        if (options?.startDate) {
            const start = moment(options.startDate).startOf('day').toDate();
            predicate = predicate.and('buildDue', '>=', start);
        }
        if (options?.endDate) {
            const end = moment(options.endDate).add(1, 'days').toDate();
            predicate = predicate.and('buildDue', '<', end);
        }
        if (options?.vIN) {
            predicate = predicate.and('vIN', FilterQueryOp.Contains, options.vIN);
        }
        let query = EntityQuery.from('OrderLineDetails').where(predicate);
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results as OrderLineDetail[];
    }

    async getPurchaseOrderStatuses() {
        const query = EntityQuery.from('PurchaseOrderStatuses').orderBy('id');
        const data = await this.manager.executeQuery(query);
        return data.results as PurchaseOrderStatus[];
    }

    async getOrderlinesByOrderId(orderId: number) {
        const predicate = new Predicate('orderId', '==', orderId);
        const query = EntityQuery.from('OrderLines').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPurchaseOrders(showCompleted: boolean, expand?: any) {
        let predicate = new Predicate('deleted', '==', null);
        if (!showCompleted) predicate = predicate.and('statusId', '!=', PurchaseOrder.Statuses.Complete);
        const query = EntityQuery.from('PurchaseOrders').where(predicate).orderByDesc('pONumber').expand(expand);
        const data = await this.manager.executeQuery(query);
        return <PurchaseOrder[]>data.results;
    }
    async getOpenPurchaseOrderInfos() {
        const query = EntityQuery.from('GetOpenPurchaseOrderInfos');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getEmailAttachments(emailMessageId: number) {
        const predicate = new Predicate('emailMessageId', '==', emailMessageId).and('deleted', '==', null);
        const query = EntityQuery.from('EmailAttachments').where(predicate).orderBy('name');
        const data = await this.manager.executeQuery(query);
        return data.results as EmailAttachment[];
    }

    async getInventoryByDateOverview() {
        const query = EntityQuery.from('InventoryByDateOverview').take(2000);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPreviousProcessQueueMessages() {
        const query = EntityQuery.from('EventLogs').where('orderLineDetailId', '!=', null).orderByDesc('dateTime').expand('orderLineDetail.orderLine.order').take(2000);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getBuiltAndNoBuiltDateOrderLineDetails() {
        const predicate = new Predicate('deleted', FilterQueryOp.Equals, null)
            .and('statusId', FilterQueryOp.NotEquals, OrderLineDetail.Statuses.Built)
            .and('built', FilterQueryOp.Equals, null)
            .and('status.countAsWIP', FilterQueryOp.Equals, false);

        const query = EntityQuery.from('OrderLineDetails').where(predicate).expand('orderLine.order.dealer').orderByDesc('created');
        const data = await this.manager.executeQuery(query);
        return data.results as OrderLineDetail[];
    }

    async getUnProcessedOrderLineDetails() {
        let predicate = new Predicate('deleted', '==', null).and('processed', '==', null).and('isMisc', '==', false);
        const query = EntityQuery.from('OrderLineDetails').where(predicate).expand('orderLine.order').orderByDesc('created');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPoisonedOrderLineDetails() {
        let predicate = new Predicate('deleted', '==', null).and('processed', '==', 99);
        const query = EntityQuery.from('OrderLineDetails').where(predicate).expand('orderLine.order').orderByDesc('created');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getMatchingOrderLineDetails(stockOrderLineDetailId: number, expand?: string | string[]) {
        const query = EntityQuery.from('MatchingOrderLineDetails').withParameters({ stockOrderLineDetailId }).expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPreviousProcessInventoryQueueMessages() {
        //TODO: how should this be filtered? Should it exclude ProcessQueue eventLogs?
        const query = EntityQuery.from('EventLogs').where('orderLineDetailId', '!=', null).orderByDesc('dateTime').expand('orderLineDetail.orderLine.order').take(2000);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getUnProcessedInventoryOrderLineDetails() {
        //TODO: after new field is added to OrderLineDetail, filter by that field
        let predicate = new Predicate('deleted', '==', null).and('processed', '==', null).and('isMisc', '==', false);
        const query = EntityQuery.from('OrderLineDetails').where(predicate).expand('orderLine.order').orderByDesc('created');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPoisonedInventoryOrderLineDetails() {
        //TODO: after new field is added to OrderLineDetail, filter by that field
        let predicate = new Predicate('deleted', '==', null).and('processed', '==', 2);
        const query = EntityQuery.from('OrderLineDetails').where(predicate).expand('orderLine.order').orderByDesc('created');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPurchaseOrderLineReceivings(expand?: any) {
        const query = EntityQuery.from('PurchaseOrderLineReceivings').orderByDesc('purchaseOrderLine.purchaseOrder.pONumber').expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getNonBilledReceivings(expand?: any) {
        const query = EntityQuery.from('GetNonBilledReceivings').orderByDesc('created').expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPurchaseOrderInvoiceLines(invoiceId: number) {
        const predicate = new Predicate('purchaseOrderInvoiceId', '==', invoiceId);
        const query = EntityQuery.from('PurchaseOrderInvoiceLines')
            .expand('purchaseOrderLineReceiving.purchaseOrderLine.part, purchaseOrderLineReceiving.purchaseOrderLine.partAlias')
            .where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results as PurchaseOrderInvoiceLine[];
    }

    async getVendorPickUpInfos(includeCompleted: boolean, expand?: any) {
        let query = EntityQuery.from('VendorPickUpInfos').orderByDesc('id').expand(expand);
        if (!includeCompleted) query = query.where('completed', '==', null);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPurchaseOrderByPartId(partId: any) {
        let predicate = new Predicate('partId', '==', partId);
        predicate = predicate.and('purchaseOrder.statusId', '!=', PurchaseOrder.Statuses.Complete);
        predicate = predicate.and('purchaseOrder.deleted', '==', null);
        //     .and('purchaseOrder.statusId', '!=', PurchaseOrder.Statuses.New)
        const query = EntityQuery.from('PurchaseOrderLines').where(predicate).expand(['purchaseOrder.vendor', 'purchaseOrder.status']);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getLastPurchaseOrderLineCost(partId: number, vendorId: number, excludePurchaseOrderId?: number) {
        let predicate = new Predicate('partId', '==', partId).and('purchaseOrder.vendorId', '==', vendorId).and('purchaseOrder.deleted', '==', null);
        if (excludePurchaseOrderId) predicate = predicate.and('purchaseOrderId', '!=', excludePurchaseOrderId);
        const query = EntityQuery.from('PurchaseOrderLines').where(predicate).orderByDesc('purchaseOrder.pODate, id').select('cost').take(1);
        const data = await this.manager.executeQuery(query);
        return data.results[0]?.cost as number | undefined;
    }
    async getPurchaseOrderByPoNumber(poNumber: any, expand?: any) {
        let predicate = new Predicate('deleted', '==', null).and('pONumber', '==', poNumber);
        let query = EntityQuery.from('PurchaseOrders').where(predicate);
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    async getPurchaseOrderLines(purchaseOrderIds: number[], expand: string | string[]) {
        const query = EntityQuery.from('PurchaseOrderLines').where('purchaseOrderId', 'in', purchaseOrderIds).expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results as PurchaseOrderLine[];
    }

    async getPurchaseOrderByPurchaseOrderInvoiceId(purchaseOrderInvoiceId: number) {
        const query = EntityQuery.from('PurchaseOrderByPurchaseOrderInvoiceId').withParameters({ purchaseOrderInvoiceId });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOpenInvoices(expand?: any, options?: any) {
        let predicate = new Predicate('deleted', '==', null).and('paid', '==', null);
        // console.log(options)
        if (options?.dealerId) predicate = predicate.and('dealerId', '==', options.dealerId);
        const query = EntityQuery.from('Invoices').where(predicate).expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getOpenInvoicesSelect() {
        const query = EntityQuery.from('GetOpenInvoices');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getOpenInvoicesForSearch(expand?: any) {
        let predicate = new Predicate('deleted', '==', null);
        let orPredicate = new Predicate('paid', '==', null).or('quickBooksId', '==', null);
        predicate = predicate.and(orPredicate);
        const query = EntityQuery.from('Invoices').where(predicate).expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getInvoiceLinesByDealerId(dealerId: number, expand?: any) {
        let predicate = new Predicate('deleted', '==', null).and('invoice.dealerId', '==', dealerId).and('invoice.deleted', '==', null);
        const query = EntityQuery.from('InvoiceLines').where(predicate).orderByDesc('invoice.invoiceDate').expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results as InvoiceLine[];
    }

    async getOrderLineDetailsByInvoiceId(invoiceId: number, expand?: any) {
        let predicate = new Predicate('deleted', '==', null).and('invoiceId', '==', invoiceId);
        const query = EntityQuery.from('OrderLineDetails').where(predicate).expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPackingSlips(expand?: any) {
        let deletedLinesPredicate = new Predicate('orderLineDetails', 'any', 'deleted', '==', null); //gets packingSlip where any are not deleted
        let predicate = new Predicate('deleted', '==', null).and(deletedLinesPredicate);

        const completedLinesPredicate = new Predicate('orderLineDetails', 'any', 'statusId', '!=', OrderLineDetail.Statuses.Complete);
        const andPredicate = new Predicate('paid', '==', null).and('dealerId', '!=', Dealer.DealerOptions.TireSales);
        const orPredicate = new Predicate('quickBooksId', '==', null).or(andPredicate).or(completedLinesPredicate);
        predicate = predicate.and(orPredicate);

        const query = EntityQuery.from('PackingSlips').where(predicate).orderByDesc('packingSlipNumber').expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPackingSlipAttachments(packingSlipId: number) {
        const predicate = new Predicate('deleted', '==', null).and('packingSlipId', '==', packingSlipId);
        const query = EntityQuery.from('PackingSlipAttachments').where(predicate).orderByDesc('name');
        const data = await this.manager.executeQuery(query);
        return data.results as PackingSlipAttachment[];
    }

    async getPackingSlipInfos(expand?: any) {
        const query = EntityQuery.from('PackingSlipInfos').orderByDesc('packingSlipNumber').expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getRecentInvoiceAndPackSlips() {
        const query = EntityQuery.from('GetRecentInvoiceAndPackSlips');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOrderLineDetailsByPackSlipId(packSlipId: number, expand?: any) {
        let predicate = new Predicate('deleted', '==', null).and('packingSlipId', '==', packSlipId);
        const query = EntityQuery.from('OrderLineDetails').where(predicate).expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getUnInvoicedOrderLineDetailsByDealerId(dealerId: number, expand?: any) {
        let predicate = new Predicate('deleted', '==', null).and('orderLine.order.dealerId', '==', dealerId).and('invoiceId', '==', null);
        let query = EntityQuery.from('OrderLineDetails').where(predicate).expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOrderLineDetailPartsByPartLinkId(partLinkId: number) {
        const predicate = new Predicate('partLinkId', '==', partLinkId)
            .and('orderLineDetail.statusId', '!=', 4)
            .and('orderLineDetail.statusId', '!=', 9)
            .and('deleted', '==', null)
            .and('orderLineDetail.orderLine.deleted', '==', null)
            .and('orderLineDetail.orderLine.order.deleted', '==', null);
        const query = EntityQuery.from('OrderLineDetailParts').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOpenOrderLineDetailByPartId(partId: number) {
        const predicate = new Predicate('orderLine.partId', '==', partId)
            .and('statusId', '!=', 4)
            .and('statusId', '!=', 9)
            .and('deleted', '==', null)
            .and('orderLine.deleted', '==', null)
            .and('orderLine.order.deleted', '==', null);
        const query = EntityQuery.from('OrderLineDetails').expand('orderLine.order').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getOpenOrderLineDetailPartsByPartId(partId: number) {
        const predicate = new Predicate('partId', '==', partId)
            .and('orderLineDetail.status.countAsWIP', '==', 1)
            // .and('orderLineDetail.statusId', '==', 10)
            // .and('orderLineDetail.statusId', '!=', 2)
            // .and('orderLineDetail.statusId', '!=', 4)
            // .and('orderLineDetail.statusId', '!=', 9)
            .and('deleted', '==', null)
            .and('orderLineDetail.deleted', '==', null)
            .and('orderLineDetail.orderLine.deleted', '==', null)
            .and('orderLineDetail.orderLine.order.deleted', '==', null);
        const query = EntityQuery.from('OrderLineDetailParts').expand('orderLineDetail.orderLine.order').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartsOverview() {
        const query = EntityQuery.from('GetPartsOverview');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartsByDateOverview(partIds: number[]) {
        const query = EntityQuery.from('GetPartsByDateOverview').withParameters({ partIds });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartsByDateOverviewGrouped(statusIds: any, typeIds: any, startDate: Date, endDate: Date) {
        const query = EntityQuery.from('GetPartsByDateOverviewGrouped').withParameters({ statusIds, typeIds, startDate, endDate });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getPartsByDateUnGrouped(statusIds: any, partIds: any) {
        console.log(partIds);
        const query = EntityQuery.from('GetPartsByDateUnGrouped').withParameters({ statusIds, partIds });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getParentOnOrderQuantities() {
        const query = EntityQuery.from('GetParentOnOrderQuantities');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getOrderLineDetailPartsOnNew() {
        const query = EntityQuery.from('GetOrderLineDetailPartsOnNew');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getPurchaseOrderInfosForParts() {
        const query = EntityQuery.from('GetPurchaseOrderInfosForParts');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOrderLineDetailPartsOverview(statusIds: number[]) {
        const query = EntityQuery.from('GetOrderLineDetailPartsOverview').withParameters({ statusIds });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOrderLineDetailsByPartAndStatusOverview(partId: number, statusId: number) {
        const query = EntityQuery.from('GetOrderLineDetailsByPartAndStatusOverview').withParameters({ partId, statusId });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartsPerOrderOverview(orderNumber: number) {
        const query = EntityQuery.from('PartsPerOrderOverview').withParameters({ orderNumber });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartPurchasesByPartId(partId: number) {
        const query = EntityQuery.from('PartPurchases').where('partId', '==', partId);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getAuditLogs(options) {
        let query = EntityQuery.from('AuditLogs');

        let hasOrderBy = false;
        if (options) {
            if (options.orderBy) {
                query = query.orderBy(options.orderBy);
                hasOrderBy = true;
            } else if (options.orderByDesc) {
                query = query.orderByDesc(options.orderByDesc);
                hasOrderBy = true;
            }

            if (options.entityTypeName) {
                query = query.where('entityTypeName', FilterQueryOp.Equals, options.entityTypeName);
            }

            if (options.id) {
                query = query.where('primaryKey', FilterQueryOp.Equals, options.id);
            }

            if (options.ids && Array.isArray(options.ids)) {
                const idsString = options.ids.map((id) => id.toString());
                query = query.where('primaryKey', 'in', idsString);
            }

            if (options.userName) {
                query = query.where('userName', FilterQueryOp.Equals, options.userName);
            }
        }

        if (!hasOrderBy) {
            query = query.orderByDesc('when');
        }

        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getHistory(table: string, ids: number[]) {
        const query = EntityQuery.from('GetHistory').withParameters({ table, ids });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getModelsOverview(statusIds: number[]) {
        const query = EntityQuery.from('GetModelsOverview').withParameters({ statusIds });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getVinsByOrderId(orderId: number) {
        const predicate = new Predicate('orderLine.orderId', '==', orderId)
            .and('orderLine.isOption', '==', false)
            .and('orderLine.isReplacement', '==', false)
            .and('orderLine.partId', '!=', null);
        const query = EntityQuery.from('OrderBuildDetail').expand('orderLine').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getBuildDetailsByOrderIds(orderIds: number[]) {
        const query = EntityQuery.from('OrderBuildDetail').expand(['orderLine.order', 'status']).where('orderLine.order.id', 'in', orderIds).where('deleted', '==', null);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getMapData(statusIds: number[]) {
        const query = EntityQuery.from('GetMapData').withParameters({ statusIds });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getVendorPickupMapData() {
        const query = EntityQuery.from('GetVendorPickupMapData');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getCustomers(expand?: any) {
        const query = EntityQuery.from('Customers').orderBy('companyName').orderBy('contactName');
        const data = await this.manager.executeQuery(query);
        return <Customer[]>data.results;
    }
    async getDealerTotal(dealerId: number | null = null) {
        const query = EntityQuery.from('GetTotalSalesForDealer').withParameters({ dealerId: dealerId });
        const data = await this.manager.executeQuery(query);
        return data.results as any[];
    }
    async getYearlySalesTotals(dealerId: number | null = null) {
        const query = EntityQuery.from('GetYearlySalesTotals');
        const data = await this.manager.executeQuery(query);
        return data.results as any[];
    }

    async getYTDTierQualifyingSales() {
        const query = EntityQuery.from('GetYTDTierQualifyingSales');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getDealerYTDTierQualifyingSales(dealerId: number) {
        const query = EntityQuery.from('GetDealerYTDTierQualifyingSales').withParameters({ dealerId: dealerId });
        const data = await this.manager.executeQuery(query);
        return data.results[0] as number;
    }
    async getDealerTierBasedOnQualifyingSales(dealerId: number) {
        const query = EntityQuery.from('GetDealerTierBasedOnQualifyingSales').withParameters({ dealerId: dealerId });
        const data = await this.manager.executeQuery(query);
        return data.results[0] as number;
    }

    async getDealerYTDSalesOrders(dealerId: number) {
        const query = EntityQuery.from('GetDealerYTDSalesOrders').withParameters({ dealerId: dealerId });
        const data = await this.manager.executeQuery(query);
        return data.results[0] as number;
    }
    async getYTDSalesOrders() {
        const query = EntityQuery.from('GetYTDSalesOrders');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getDealerYTDPayments(dealerId: number) {
        const query = EntityQuery.from('GetDealerYTDPayments').withParameters({ dealerId: dealerId });
        const data = await this.manager.executeQuery(query);
        return data.results[0] as number;
    }
    async getDealerYTDInvoices(dealerId: number) {
        const query = EntityQuery.from('GetDealerYTDInvoices').withParameters({ dealerId: dealerId });
        const data = await this.manager.executeQuery(query);
        return data.results[0] as number;
    }

    async getDealerSalesTotal(dealerId: number) {
        const query = EntityQuery.from('GetDealerSalesSummary').withParameters({ dealerId: dealerId });
        const data = await this.manager.executeQuery(query);
        return data.results as any[];
    }

    async getDealers(showAll?: boolean, expand?: string) {
        let predicate = new Predicate('deleted', '==', null);
        if (!showAll) predicate = predicate.and('inactive', '==', null);
        const query = EntityQuery.from('Dealers')
            .expand('shipAddresses' + (expand ? `, ${expand}` : ''))
            .orderBy('companyName')
            .where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getDealerInfos() {
        let predicate = new Predicate('deleted', '==', null);
        const query = EntityQuery.from('Dealers').orderBy('companyName').where(predicate).select('id, companyName');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getDealerEntries(expand?: string) {
        let predicate = new Predicate('deleted', '==', null).and('inactive', '==', null);
        const month = moment().add(-1, 'month').toDate();
        const orPredicate = new Predicate('validated', '==', null);
        predicate = predicate.and(orPredicate);
        const query = EntityQuery.from('Dealers').expand(expand).orderBy('companyName').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getDealerAttachments(dealerId: number) {
        const predicate = new Predicate('dealerId', '==', dealerId).and('deleted', '==', null);
        const query = EntityQuery.from('DealerAttachments').where(predicate).orderBy('name');
        const data = await this.manager.executeQuery(query);
        return data.results as DealerAttachment[];
    }

    async getLatestAchInfo(dealerId: number) {
        const query = EntityQuery.from('GetLatestAchInfo').withParameters({ dealerId });
        const data = await this.manager.executeQuery(query);
        return data.results[0] as any;
    }

    async getAchBankInfo(code: Guid) {
        const query = EntityQuery.from('GetAchBankInfo').withParameters({ code });
        const data = await this.manager.executeQuery(query);
        return data.results[0] as any;
    }

    async getShipAddresses(dealerId?: number) {
        let query = EntityQuery.from('ShipAddresses');
        if (dealerId) query = query.where('dealerId', '==', dealerId);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getVendors(excludeInactive?: boolean) {
        let query = EntityQuery.from('Vendors').orderBy('companyName').where('deleted', '==', null);
        if (excludeInactive) query = query.where('inactive', '==', null);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getSalesReps() {
        let query = EntityQuery.from('SalesReps').orderBy('name').where('deleted', '==', null);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getContactLogs(expand?: any) {
        let predicate = new Predicate('deleted', '==', null);
        let query = EntityQuery.from('ContactLogs').where(predicate);
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getVendorByName(name: any) {
        const query = EntityQuery.from('Vendors').where('companyName', '==', name).take(1);
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    async getPurchaseOrderPickUpsByPoLine(poLineId: number) {
        const linePredicate = new Predicate('purchaseOrderLineId', '==', poLineId).and('deleted', '==', null);
        const predicate = new Predicate('deleted', '==', null).and('lines', 'any', linePredicate);
        const query = EntityQuery.from('PurchaseOrderPickUps').where(predicate).expand('lines.purchaseOrderLine');
        const data = await this.manager.executeQuery(query);
        return data.results as PurchaseOrderPickUp[];
    }
    async getPurchaseOrderPickUpLinePOLine(poLineId: number) {
        const predicate = new Predicate('purchaseOrderLineId', '==', poLineId).and('deleted', '==', null).and('completed', '==', null);
        const query = EntityQuery.from('PurchaseOrderPickUpLines').where(predicate).expand('purchaseOrderPickUp');
        const data = await this.manager.executeQuery(query);
        return data.results as PurchaseOrderPickUpLine[];
    }

    async getOptionsForOrderLine(parentLineId: number) {
        const predicate = new Predicate('parentLineId', '==', parentLineId).and('isOption', '==', true).and('deleted', '==', null);
        const query = EntityQuery.from('OrderLines').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getOptionsForOrderLineDetail(parentLineId: number) {
        const predicate = new Predicate('parentLineId', '==', parentLineId).and('isOption', '==', true);
        const query = EntityQuery.from('OrderLineDetails').expand('orderLine.order').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getCustomerOrderList(customerId: number) {
        const query = EntityQuery.from('OrderList').where('customerId', '==', customerId);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getEmailAddresses() {
        const query = EntityQuery.from('EmailAddresses').where('deleted', '==', null);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getSettings() {
        const query = EntityQuery.from('Settings');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getSettingsByName(name: string) {
        const query = EntityQuery.from('Settings').where('name', '==', name);
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    // async getCustomerContactsById(customerId: number) {
    //     const query = EntityQuery.from('CustomerContacts')
    //         .expand('customer')
    //         .where('customerId', '==', customerId);
    //     const data = await this.manager.executeQuery(query);
    //     return <CustomerContact[]>data.results;
    // }

    async getCustomerLocations() {
        const query = EntityQuery.from('CustomerLocations').expand('customer');
        const data = await this.manager.executeQuery(query);
        return <CustomerLocation[]>data.results;
    }

    async getCustomerLocationsById(customerId: number) {
        const query = EntityQuery.from('CustomerLocations').where('customerId', '==', customerId);
        const data = await this.manager.executeQuery(query);
        return <CustomerLocation[]>data.results;
    }

    async getNextOrderNumber(customerId?: number) {
        const query = EntityQuery.from('GetNextOrderNumber');
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }
    async getNextPackingSlipNumber(customerId?: number) {
        const query = EntityQuery.from('GetNextPackingSlipNumber');
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    async getDealersForExport() {
        const query = EntityQuery.from('GetDealersForExport');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPackingSlipByFreightInvoiceId(invoiceId: number) {
        const query = EntityQuery.from('GetPackingSlipByFreightInvoiceId').withParameters({ invoiceId });
        const data = await this.manager.executeQuery(query);
        return data.results as PackingSlip[];
    }

    async getOpenInvoicePaymentInfos(dealerId: number) {
        const query = EntityQuery.from('OpenInvoicePaymentInfos').withParameters({ dealerId });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getInvoicePaymentInfos(paymentId: number) {
        const query = EntityQuery.from('InvoicePaymentInfos').withParameters({ paymentId });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPaymentCreditsForDealerId(dealerId: number, expand?: any) {
        let predicate = new Predicate('deleted', '==', null).and('dealerId', '==', dealerId).and('availableAmount', '>', 0);
        let query = EntityQuery.from('PaymentCredits').where(predicate);
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPaymentCreditsForInvoiceId(invoiceId: number, dealerId: number, expand?: any) {
        const predicate = new Predicate('deleted', '==', null).and('dealerId', '==', dealerId);
        let query = EntityQuery.from('PaymentCredits').where(predicate);
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return (data.results as any[]).filter((credit) => credit.creditOrder?.orderLines?.some((line) => line.returnedDetail?.invoiceId === invoiceId));
    }

    async getInvoicesForExport() {
        const query = EntityQuery.from('GetInvoicesForExport');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getVendorsForExport() {
        const query = EntityQuery.from('GetVendorsForExport');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getPurchaseOrdersForExport() {
        const query = EntityQuery.from('GetPurchaseOrdersForExport');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async changeUserPassword(user, password) {
        const query = EntityQuery.from('ChangeUserPassword').withParameters({ id: user.id, password: password }).using(this.manager);
        const data = await this.manager.executeQuery(query);
        if (data.results && data.results.length > 0) return data.results[0];
        else return null;
    }

    async getDistinctPackSlips(packingSlipNumber?: number) {
        const query = EntityQuery.from('PackingSlips').where('quickBooksId', '==', null).orderByDesc('packingSlipNumber').select('packingSlipNumber, dealer.companyName');
        const data = (await this.manager.executeQuery(query)) as any;
        return data.results;
    }

    async getPackSlipByNumber(packingSlipNumber?: number, expand?: any) {
        let query = EntityQuery.from('PackingSlips').where('packingSlipNumber', '==', packingSlipNumber);
        if (expand) query = query.expand(expand);
        const data = (await this.manager.executeQuery(query)) as any;
        return data.results[0];
    }

    async getPackSlipByInvoiceNumber(invoiceNumber: number, expand?: any) {
        let query = EntityQuery.from('PackingSlips').where('invoiceNumber', '==', invoiceNumber);
        if (expand) query = query.expand(expand);
        const data = (await this.manager.executeQuery(query)) as any;
        return data.results[0];
    }

    async getInvoiceByInvoiceNumber(invoiceNumber: number, expand?: any) {
        let predicate = new Predicate('invoiceNumber', '==', invoiceNumber).and('deleted', '==', null);
        let query = EntityQuery.from('Invoices').where(predicate);
        if (expand) query = query.expand(expand);
        const data = (await this.manager.executeQuery(query)) as any;
        return data.results[0];
    }

    async getPackSlipByDealer(dealerId: number) {
        const query = EntityQuery.from('PackingSlips').where('dealerId', '==', dealerId).orderByDesc('id');
        const data = (await this.manager.executeQuery(query)) as any;
        return data.results;
    }

    async getSalesOrderPaymentCheck(orderId: number) {
        const query = EntityQuery.from('SalesOrderPaymentCheck').withParameters({ orderId: orderId });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPaymentLinesByInvoiceId(invoiceId: number, expand?: string) {
        const predicate = new Predicate('invoiceId', '==', invoiceId).and('deleted', '==', null);
        const query = EntityQuery.from('PaymentLines').where(predicate).expand(expand);
        const data = (await this.manager.executeQuery(query)) as any;
        return data.results;
    }

    async getPaymentLinesByPaymentIds(paymentIds: number[], expand?: string) {
        const predicate = new Predicate('paymentId', 'in', paymentIds).and('deleted', '==', null);
        const query = EntityQuery.from('PaymentLines').where(predicate).expand(expand);
        const data = (await this.manager.executeQuery(query)) as any;
        return data.results;
    }

    async getOrderByInvoiceNumber(invoiceNumber: number, expand?: any) {
        let query = EntityQuery.from('Orders').where('invoiceNumber', '==', invoiceNumber);
        if (expand) query = query.expand(expand);
        const data = (await this.manager.executeQuery(query)) as any;
        return data.results[0];
    }

    async getOrdersByInvoiceNumbers(invoiceNumbers: any[]) {
        const query = EntityQuery.from('Orders').where('invoiceNumber', 'in', invoiceNumbers).expand('status');
        const data = (await this.manager.executeQuery(query)) as any;
        return data.results;
    }

    async getProductionSchedule(jigId: number, runDate: Date, expand?: any) {
        let start = moment(runDate).startOf('day');
        let end = moment(runDate).add(1, 'days').startOf('day');
        let predicate = new Predicate('jigId', '==', jigId);
        predicate = predicate.and('buildDate', '>=', start).and('buildDate', '<', end);
        let query = EntityQuery.from('AssemblySchedules').where(predicate).expand(expand).orderBy('jigId');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    // SM 10/8 - No longer being used task #57577
    async getPartInvoiceById(partId: number) {
        const predicate = new Predicate('partId', '==', partId).and('order.deleted', '==', null);
        const query = EntityQuery.from('OrderLines')
            .where(predicate)
            .select(['order.invoiceNumber', 'order.id', 'order.createDate', 'order.createdBy.firstName', 'order.createdBy.lastName']);
        const data = await this.manager.executeQuery(query);
        const sortedArray = [];
        let result = [];
        let previousOrder: any;

        data.results.forEach((order: any) => {
            if (!previousOrder || previousOrder.order_Id !== order.order_Id) {
                sortedArray.push(order);
                previousOrder = order;
            }
        });

        result = sortedArray.map((invoice) => {
            return {
                type: 'Invoice',
                date: invoice.order_CreateDate,
                user: invoice.order_CreatedBy_FirstName + ' ' + invoice.order_CreatedBy_LastName,
                id: invoice.order_Id,
                detail: invoice.order_InvoiceNumber,
                url: '/sales-orders/' + invoice.order_Id,
            };
        });

        return result;
        // query.from('orderlines').where('partId', ==, partId).select('order.invoiceNumber');
    }

    async getPartChildren(partId: number) {
        const query = EntityQuery.from('Parts').where('parentPartId', '==', partId).expand('purchaseOrderLines.purchaseOrder');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getActivePartAliases(options?: any) {
        const predicate = new Predicate('deleted', '==', null);
        let query = EntityQuery.from('PartAliases').where(predicate);
        if (options) {
            if (options.select) query = query.select(options.select);
            if (options.orderBy) query = query.orderBy(options.orderBy);
        }
        const data = await this.manager.executeQuery(query);
        return data.results as PartAlias[];
    }
    async getPartAliases(partId: number) {
        const predicate = new Predicate('partId', '==', partId).and('deleted', '==', null);
        const query = EntityQuery.from('PartAliases').where(predicate).expand('vendor');
        const data = await this.manager.executeQuery(query);
        return data.results as PartAlias[];
    }

    async getFrames() {
        const query = EntityQuery.from('GetFrames');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getACHPaymentByOrderId(orderId: number) {
        const predicate = new Predicate('orderId', '==', orderId).and('deleted', '==', null);
        const query = EntityQuery.from('OrderAchPayments').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPayments(recent: boolean = true, expand?: string, take?: number) {
        let query = EntityQuery.from('Payments').where('deleted', '==', null).orderByDesc('paymentDate, id');
        if (recent) {
            const oneMonthAgo = moment(new Date()).add(-1, 'months').startOf('day').toDate();
            const predicate = new Predicate('quickBooksLastExport', '==', null).or('quickBooksLastExport', '>=', oneMonthAgo);
            query = query.where(predicate);
        }
        if (expand) query = query.expand(expand);
        else query = query.expand('dealer');
        if (take) query = query.take(take);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPaymentCredits() {
        const predicate = new Predicate('deleted', '==', null).and('created', '>=', new Date(2025, 1, 1));
        const query = EntityQuery.from('PaymentCredits').where(predicate).expand('dealer').orderBy('created');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPaymentCreditsByDealerId(dealerId: number) {
        const predicate = new Predicate('deleted', '==', null).and('dealerId', '==', dealerId);
        const query = EntityQuery.from('PaymentCredits').where(predicate).expand('paymentCreditType').orderBy('created');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getDealerOverPayments(dealerId: number) {
        const query = EntityQuery.from('GetDealerOverPayments').withParameters({ dealerId: dealerId });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartByPartOptionsId(partId: number) {
        const predicate = new Predicate('optionPartId', '==', partId).and('deleted', '==', null).and('part.deleted', '==', null);
        const query = EntityQuery.from('PartOptions')
            .where(predicate)
            .select(['part.partNumber', 'part.id', 'part.description', 'part.isAssembly', 'part.created', 'part.description']);
        const data = await this.manager.executeQuery(query);
        let result = [];

        result = data.results.map((part: any) => {
            return {
                type: 'Part Option',
                date: part.part_Created ? part.part_created : 'N/A',
                user: 'N/A',
                id: part.part_Id,
                subId: null,
                detail: part.part_PartNumber,
                url: '/parts/' + part.part_Id,
                isAssembly: part.part_IsAssembly,
                description: part.part_Description,
            };
        });

        return result;
    }

    async getDeliveryStackInformation(deliveryId: number, expand?: string) {
        let predicate = new Predicate('deleted', '==', null).and('deliveryId', '==', deliveryId);
        let query = EntityQuery.from('DeliveryStacks').where(predicate).orderByDesc('created');
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getDeliveryStacks(expand?: string, showCompleted?: boolean) {
        let predicate = new Predicate('deleted', '==', null);
        if (showCompleted) predicate = predicate.and('stackCompleted', '!=', null);
        let query = EntityQuery.from('Deliveries').where(predicate).orderBy('created');
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getDeliveries(expand?: string, showCompleted?: boolean, mode?: 'stack' | 'delivery') {
        let predicate = new Predicate('deleted', '==', null);
        if (showCompleted) predicate = predicate.and('completed', '!=', null);
        if (mode === 'stack') predicate = predicate.and('stackCompleted', '==', null);
        if (mode === 'delivery') {
            predicate = predicate.and('stackCompleted', '!=', null);
            predicate = predicate.and('completed', '==', null);
        }
        let query = EntityQuery.from('Deliveries').where(predicate).orderByDesc('created');
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartByPartLinkId(partId: number) {
        let predicate = new Predicate('partId', '==', partId).or('subPartId', '==', partId);
        predicate = predicate.and('deleted', '==', null).and('part.deleted', '==', null).and('subPart.deleted', '==', null);
        const query = EntityQuery.from('PartLinks')
            .where(predicate)
            .select(['part.partNumber', 'part.id', 'part.description', 'part.isAssembly', 'created', 'createdBy', 'subPart.id', 'part.description', 'quantity']);
        const data = await this.manager.executeQuery(query);
        let result = [];

        result = data.results.map((part: any) => {
            return {
                type: 'Part Links',
                date: part.created ? part.created : 'N/A',
                user: part.createdBy ? part.createdBy : 'N/A',
                id: part.part_Id,
                subId: part.subPart_Id,
                detail: part.part_PartNumber,
                url: '/parts/' + part.part_Id,
                isAssembly: part.part_IsAssembly,
                description: part.part_Description,
                quantity: part.quantity,
            };
        });

        return result;
    }

    async getPartTreeChildren(partId: number) {
        const query = EntityQuery.from('PartChildren').withParameters({ partId });
        const data = await this.manager.executeQuery(query);
        return data.results as Part[];
    }

    // used for creating the part details tree page
    async createOrderLine(
        partId: number,
        order: any = null,
        quantity: number = 1,
        extendedQuantity: number = 1,
        parentOrderLine: any = null,
        partLinkIdSource: any = null,
        detached: boolean = false,
        isOption: boolean = false,
    ) {
        const children = await this.getPartTreeChildren(partId);
        const part = children.find((c) => c.id === partId);
        return this.createOrderLineForPart(part, order, quantity, extendedQuantity, parentOrderLine, partLinkIdSource, detached, isOption);
    }

    async createOrderLineForPart(
        part: Part,
        order: Order = null,
        quantity: number = 1,
        extendedQuantity: number = 1,
        parentOrderLine: any = null,
        partLinkIdSource: any = null,
        detached: boolean = false,
        isOption: boolean = false,
    ) {
        if (!part) return null;

        // for the part, add a order line
        let lineNumber = 1;
        if (!!order) lineNumber = order.getNextLineNumber();
        const orderLine: any = this.orderLines.createEntity();
        if (detached) orderLine.entityAspect.setDeleted();
        else orderLine.part = part;
        orderLine.partId = part.id;
        orderLine.order = order;
        orderLine.parentLine = parentOrderLine;
        orderLine.quantity = quantity;
        orderLine.extendedQuantity = extendedQuantity;
        orderLine.partNumber = part.partNumber;
        orderLine.description = part.description;
        orderLine.cost = part.cost;
        orderLine.calculatedCost = part.calculatedCost;
        orderLine.price = part.price;
        orderLine.lineNumber = lineNumber;
        orderLine.partLinkIdSource = partLinkIdSource;
        orderLine.isOption = isOption;
        orderLine.category = part.category;
        orderLine.isAssembly = part.isAssembly;

        for (let partLink of part.partLinks) {
            if (!partLink.deleted)
                await this.createOrderLineForPart(partLink.subPart, order, partLink.quantity, extendedQuantity * partLink.quantity, orderLine, partLink.id, detached);
        }

        return orderLine;
    }

    async getWeldShopParts(categories: string[]) {
        const query = EntityQuery.from('WeldShopParts').withParameters({ categories });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getMapDataByDealer(dealerId: number, statusIds: number[]) {
        const query = EntityQuery.from('GetMapDataByDealer')
            .withParameters({ dealerId: dealerId, statusIds: statusIds })
            .expand(['orderLine.order.confirmations', 'orderLine.order.dealer', 'notes.createdBy', 'optionDetails.orderLine']);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartServiceCharges() {
        const query = EntityQuery.from('PartServiceCharges').where('deleted', '==', null).orderBy('displayOrder, name');
        const data = await this.manager.executeQuery(query);
        return data.results as PartServiceCharge[];
    }

    async getPartServiceChargeLinks(partId: number) {
        const predicate = new Predicate('partId', '==', partId).and('deleted', '==', null);
        const query = EntityQuery.from('PartPartServiceChargeLinks').where(predicate).orderBy('partServiceCharge.displayOrder, partServiceCharge.name').expand('partServiceCharge');
        const data = await this.manager.executeQuery(query);
        return data.results as PartPartServiceChargeLink[];
    }

    async getPhotos(linkId?: number, type?: 'Order' | 'Pack Slip' | 'Purchase Order' | 'Delivery', expand?: any) {
        let query = EntityQuery.from('Photos').where('deleted', '==', null).orderBy('created desc, name');
        if (linkId && type) {
            const field = type === 'Delivery' ? 'deliveryId' : type === 'Order' ? 'orderId' : type === 'Pack Slip' ? 'packingSlipId' : 'purchaseOrderId';
            query = query.where(field, '==', linkId);
        }
        if (expand) query = query.expand(expand);

        const data = await this.manager.executeQuery(query);
        return data.results as Photo[];
    }

    async getReportGuidsByInvoiceIds(invoiceIds: number[]) {
        const query = EntityQuery.from('PackingSlips').where('id', 'in', invoiceIds).select('reportGuid');
        const data = await this.manager.executeQuery(query);
        return data.results.map((x) => x.reportGuid) as Guid[];
    }

    async getWarrantiesForReport(filter?: any) {
        var query = EntityQuery.from('GetWarrantiesForReport');
        if (filter.startDate) {
            query = query.where('createDate', '>', filter.startDate);
        } else {
            query = query.where('createDate', '>', new Date(2024, 1, 1));
        }
        if (!!filter?.endDate) {
            query = query.where('createDate', '<', filter.endDate as Date);
        }
        if (!!filter?.dealerId) {
            query = query.where('dealerId', '==', filter.dealerId as Date);
        }
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getOrderLineDetailPartInfos(startDate: Date, endDate: Date, partId: number, includeDeleted: boolean) {
        const query = EntityQuery.from('OrderLineDetailPartInfos').withParameters({ startDate, endDate, partId, includeDeleted });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPurchaseOrderLineReceivingInfos(startDate: Date, endDate: Date, partId: number) {
        const query = EntityQuery.from('PurchaseOrderLineReceivingInfos').withParameters({ startDate, endDate, partId });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartPurchaseInfos(startDate: Date, endDate: Date, partId: number) {
        const query = EntityQuery.from('PartPurchaseInfos').withParameters({ startDate, endDate, partId });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartNumbers() {
        const query = EntityQuery.from('PartNumbers');
        const data = await this.manager.executeQuery(query);
        return data.results as string[];
    }

    async getDuplicateReferenceNumberCount(dealerId: number, referenceNumber: string) {
        const query = EntityQuery.from('GetDuplicateReferenceNumberCount').withParameters({ dealerId, referenceNumber });
        const data = await this.manager.executeQuery(query);
        return data.results[0];
    }

    async getInvoiceAttachments(invoiceId: number) {
        const predicate = new Predicate('invoiceId', '==', invoiceId).and('deleted', '==', null);
        const query = EntityQuery.from('InvoiceAttachments').where(predicate).orderBy('name');
        const data = await this.manager.executeQuery(query);
        return data.results as InvoiceAttachment[];
    }

    async getPartConfigAttachments() {
        const predicate = new Predicate('deleted', '==', null).and('partConfigurationId', '==', null);
        const query = EntityQuery.from('PartConfigAttachments').where(predicate).orderBy('name');
        const data = await this.manager.executeQuery(query);
        return data.results as PartConfigAttachment[];
    }
    async getPartConfigurations(expand?: string) {
        const predicate = new Predicate('deleted', '==', null).and('processed', '==', null);
        const query = EntityQuery.from('PartConfigurations').where(predicate).expand(expand).orderBy('partNumber');
        const data = await this.manager.executeQuery(query);
        return data.results as PartConfiguration[];
    }

    async getJigTeamMembers() {
        const query = EntityQuery.from('GetJigTeamMembers');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getAssemblyJigTeamMembersByDate(date: Date) {
        const start = moment(date).startOf('day');
        const end = moment(date).endOf('day');
        let predicate = new Predicate('deleted', '==', null).and('scheduled', '>=', start).and('scheduled', '<', end);
        const query = EntityQuery.from('AssemblyJigMembers').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getAssemblyJigNotesByDate(date: Date) {
        const start = moment(date).startOf('day');
        const end = moment(date).endOf('day');
        let predicate = new Predicate('deleted', '==', null).and('scheduled', '>=', start).and('scheduled', '<', end);
        const query = EntityQuery.from('AssemblyJigNotes').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getContacts(entityId: number, entityType: 'Dealer' | 'Vendor') {
        let query = EntityQuery.from('Contacts').where('deleted', '==', null);
        if (entityType === 'Dealer') query = query.where('dealerId', '==', entityId);
        else if (entityType === 'Vendor') query = query.where('vendorId', '==', entityId);
        const data = await this.manager.executeQuery(query);
        return data.results as Contact[];
    }

    async getCustomerReviews() {
        const query = EntityQuery.from('CustomerReviews');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getWarrantyRegistrations(expand?: string | string[]) {
        let predicate = new Predicate('deleted', '==', null);
        const query = EntityQuery.from('WarrantyRegistrations').where(predicate).expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getPartAttachments(partId: number) {
        let predicate = new Predicate('deleted', '==', null).and('partId', '==', partId);
        const query = EntityQuery.from('PartAttachments').where(predicate).orderByDesc('created');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getNotes(entityType?: string, primaryKey?: number) {
        let predicate = new Predicate('deleted', '==', null);
        if (entityType) {
            predicate = predicate.and('entityTypeName', '==', entityType);
        }
        if (primaryKey) {
            predicate = predicate.and('primaryKey', '==', primaryKey);
        }
        let query = EntityQuery.from('Notes').orderByDesc('created').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results as Note[];
    }

    async getDetailsForSalesByPartReport(startDate: Date, endDate: Date) {
        var query = EntityQuery.from('GetDetailsForSalesByPartReport').withParameters({ startDate, endDate });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getExpenseReports(expand?: string) {
        let predicate = new Predicate('deleted', '==', null);
        const query = EntityQuery.from('ExpenseReports').expand(expand).where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getInvoiceNotes(invoiceId: number) {
        var query = EntityQuery.from('GetInvoiceNotes').withParameters({ invoiceId });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getInventoryProjections(showCompleted?: boolean) {
        let predicate = new Predicate('deleted', '==', null);
        const query = EntityQuery.from('InventoryProjections').orderByDesc('id').where(predicate);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getInventoryProjectionsInfosForParts(projectionIds: number[]) {
        const query = EntityQuery.from('GetInventoryProjectionParts').withParameters({ projectionIds });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
    async getModelSizesByModelType(modelTypeId: number) {
        const query = EntityQuery.from('PartConfigModelSizes').where('deleted', '==', null).where('partConfigModelTypeId', '==', modelTypeId).expand('partConfigSizeLinks');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }
}
